import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

export const useSolicitariAchizitie = () => {
  const { token } = useStateContext();
  const [furnizori, setFurnizori] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productRequestStatuses, setProductRequestStatuses] = useState([]);
  const [purchaseRequestStatuses, setPurchaseRequestStatuses] = useState([]);
  const [overallSummary, setOverallSummary] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const [filters, setFilters] = useState({
    search: '',
    furnizor_id: null,
    product_in_purchase_statuses: [1,2,3,4,5,6,7], // toate statusurile
    date_start: null,
    date_end: null,
    sort_by: 'furnizor_name',
    sort_order: 'asc',
    oferts_ids_list: null, // noul filtru pentru ID-urile comenzilor
    purchase_request_ids_list: null // noul filtru pentru ID-urile cererilor de achiziție
  });

  // Funcție pentru aplicarea filtrelor
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current: 1 })); // Resetăm la prima pagină
  }, []);

  // Funcție pentru resetarea filtrelor
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      furnizor_id: null,
      product_in_purchase_statuses: [1,2,3,4,5,6,7],
      date_start: null,
      date_end: null,
      sort_by: 'furnizor_name',
      sort_order: 'asc',
      oferts_ids_list: null,
      purchase_request_ids_list: null
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  }, []);

  // Funcție pentru schimbarea paginii
  const handleTableChange = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  // Funcția principală pentru fetch-ul datelor
  const fetchPurchaseRequestsGrouped = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const params = new URLSearchParams({
        token,
        page: pagination.current.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      });

      // Adăugăm filtrele
      if (filters.search) params.append('search', filters.search);
      if (filters.furnizor_id) params.append('furnizor_id', filters.furnizor_id);
      if (filters.product_in_purchase_statuses && filters.product_in_purchase_statuses.length > 0) {
        params.append('product_in_purchase_statuses', filters.product_in_purchase_statuses.toString());
      }
      if (filters.date_start) params.append('date_start', filters.date_start);
      if (filters.date_end) params.append('date_end', filters.date_end);
      if (filters.oferts_ids_list) params.append('oferts_ids_list', filters.oferts_ids_list);
      if (filters.purchase_request_ids_list) params.append('purchase_request_ids_list', filters.purchase_request_ids_list);

      console.log('🔄 Fetching purchase requests grouped by furnizor...', params.toString());

      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_purchase_requests_grouped_by_furnizor?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Purchase requests data received:', data);
        
        setFurnizori(data.items || []);
        setOverallSummary(data.summary || null);
        
        // Actualizăm paginația
        setPagination(prev => ({
          ...prev,
          current: data.page || 1,
          total: data.total || 0,
          pageSize: data.page_size || prev.pageSize
        }));

      } else {
        console.error('❌ HTTP error:', response.status, response.statusText);
        message.error('Eroare la încărcarea solicitărilor de achiziție');
      }
    } catch (error) {
      console.error('❌ Network error:', error);
      message.error('Eroare de conexiune');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.current, pagination.pageSize, filters]);

  // Funcție pentru obținerea statusurilor produselor din purchase requests
  const fetchProductRequestStatuses = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_purchase_request_statuses?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProductRequestStatuses(data);
      } else {
        console.error('Eroare la încărcarea statusurilor produselor');
      }
    } catch (error) {
      console.error('Eroare la încărcarea statusurilor produselor:', error);
    }
  }, [token]);

  // Funcție pentru obținerea statusurilor purchase requests
  const fetchPurchaseRequestStatuses = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/purchase-request-status?token=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPurchaseRequestStatuses(data);
      } else {
        console.error('Eroare la încărcarea statusurilor cerilor de achiziție');
      }
    } catch (error) {
      console.error('Eroare la încărcarea statusurilor cerilor de achiziție:', error);
    }
  }, [token]);

  // Funcție pentru schimbarea statusului unui produs din purchase request
  const changeProductRequestStatus = useCallback(async (productRequestId, newStatusId, purchaseRequestId) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/purchase-requests/${purchaseRequestId}/produse/${productRequestId}?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status_id: newStatusId
        })
      });

      if (response.ok) {
        // Actualizare locală în furnizori
        setFurnizori(prevFurnizori => 
          prevFurnizori.map(furnizor => ({
            ...furnizor,
            purchase_requests: furnizor.purchase_requests.map(request => ({
              ...request,
              products: request.products.map(product => 
                product.id === productRequestId 
                  ? { ...product, status_id: newStatusId, date_updated: new Date().toISOString() }
                  : product
              )
            }))
          }))
        );
        
        message.success('Statusul produsului a fost actualizat cu succes!');
        return true;
      } else {
        message.error('Eroare la actualizarea statusului produsului');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea statusului produsului:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token]);

  // Funcție pentru schimbarea statusului unui purchase request întreg
  const changePurchaseRequestStatus = useCallback(async (purchaseRequestId, newStatusId) => {
    if (!token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/purchase-requests/${purchaseRequestId}/status?token=${token}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status_id: newStatusId
        })
      });

      if (response.ok) {
        // Actualizare locală în furnizori
        setFurnizori(prevFurnizori => 
          prevFurnizori.map(furnizor => ({
            ...furnizor,
            purchase_requests: furnizor.purchase_requests.map(request => 
              request.id === purchaseRequestId 
                ? { ...request, status_id: newStatusId, date_updated: new Date().toISOString() }
                : request
            )
          }))
        );
        
        message.success('Statusul cererii de achiziție a fost actualizat cu succes!');
        return true;
      } else {
        message.error('Eroare la actualizarea statusului cererii de achiziție');
        return false;
      }
    } catch (error) {
      console.error('Eroare la actualizarea statusului cererii de achiziție:', error);
      message.error('Eroare de conexiune');
      return false;
    }
  }, [token]);

  // Auto-loading la mount și la schimbarea dependințelor
  useEffect(() => {
    fetchPurchaseRequestsGrouped();
  }, [fetchPurchaseRequestsGrouped]);

  // Loading statusurilor la mount
  useEffect(() => {
    fetchProductRequestStatuses();
    fetchPurchaseRequestStatuses();
  }, [fetchProductRequestStatuses, fetchPurchaseRequestStatuses]);

  return {
    furnizori,
    loading,
    pagination,
    productRequestStatuses,
    purchaseRequestStatuses,
    overallSummary,
    filters,
    applyFilters,
    clearFilters,
    handleTableChange,
    fetchPurchaseRequestsGrouped,
    fetchProductRequestStatuses,
    fetchPurchaseRequestStatuses,
    changeProductRequestStatus,
    changePurchaseRequestStatus,
    setFurnizori
  };
}; 